<template>
	<Draggable
		class="column__draggable-wrapper"
		:class="{ 'column__draggable-wrapper--readonly': readonly }"
		@dragend="emit('dragend')"
		@dragover="emit('dragover', $event)"
		@dragstart="emit('dragstart')">
		<Card class="column">
			<template #content>
				<div
					class="column__container"
					:class="{ 'column__container--readonly': readonly }"
					@click="readonly ? null : emit('click')">
					<div class="column__header">
						<div class="column__type-container">
							<FontAwesomeIcon
								class="column__icon"
								:icon="'fal ' + iconMap[column.type]" />
						</div>
						<div class="column__name-container">
							<div class="column__name">{{ column.displayName }}</div>
							<div class="column__type">{{ camelCaseToLabel(column.type) }}</div>
						</div>
						<div class="column__right">
							<Tag
								v-if="module.model.title === column.name"
								class="column__tag"
								icon="fal fa-star"
								severity="secondary"
								value="Title"></Tag>
							<Button
								v-if="!readonly"
								icon="fal fa-trash"
								rounded
								severity="secondary"
								text
								@click.stop="emit('delete')" />
						</div>
					</div>
				</div>
			</template>
		</Card>
	</Draggable>
</template>

<script setup lang="ts">
import { camelCaseToLabel } from '@codecannon/schemas/utils/strings'
import Card from 'primevue/card'
import Tag from 'primevue/tag'
import Button from 'primevue/button'
import Draggable from '@/helpers/dragAndDrop/Draggable.vue'
import type { EnrichedModule } from '@codecannon/schemas/schemas/EnrichedModule'
import { EnrichedColumn } from '@codecannon/schemas/schemas/EnrichedColumn'
import { iconMap } from '@codecannon/schemas/helpers/typeMap'
import FontAwesomeIcon from '@/components/FontAwesomeIcon.vue'

const emit = defineEmits<{
	(e: 'delete'): void
	(e: 'dragover', event: DragEvent): void
	(e: 'dragstart'): void
	(e: 'dragend'): void
	(e: 'click'): void
}>()

defineProps<{
	module: EnrichedModule
	column: EnrichedColumn
	readonly?: boolean
}>()
</script>

<style scoped lang="scss">
.column__draggable-wrapper {
	margin-bottom: 12px;

	&--readonly {
		pointer-events: none !important;
	}
}

.column {
	overflow: hidden;

	&:deep(.p-card-body) {
		padding: 0;

		.p-inplace-content,
		.p-inplace-display {
			display: flex;
			flex-direction: column;
			gap: 16px;
			padding: 20px;
		}
	}

	.column__container {
		cursor: pointer;
		padding: 20px;

		@media all {
			background: var(--p-surface-800);
		}

		&:hover {
			background: var(--p-surface-50);

			@media all {
				background: var(--p-surface-700);
			}
		}

		&--readonly {
			cursor: default;

			&:hover {
				background: var(--p-surface-0);

				@media all {
					background: var(--p-surface-800);
				}
			}
		}
	}

	.column__header {
		display: flex;
		align-items: center;
		gap: 16px;
		flex: 1;

		.column__right {
			display: flex;
			align-items: center;
			gap: 8px;

			.column__tag {
				background: transparent;
			}
		}

		.column__type-container {
			display: flex;
			align-items: center;
			justify-content: center;
			width: 40px;
			height: 40px;

			.column__icon {
				font-size: 34px;
				color: var(--p-text-color);
			}
		}

		.column__name-container {
			display: flex;
			flex-direction: column;
			align-items: flex-start;
			flex: 1;

			.column__name {
				font-size: 16px;
				flex: 1;
			}

			.column__type {
				margin-top: -4px;
				font-size: 12px;
				flex: 1;
				text-align: center;
				color: var(--p-blue-500);
			}

			.column__name-container-name {
				display: flex;
				flex-direction: row;
				align-items: center;
				gap: 8px;
			}
		}
	}

	.column__add-validator {
		width: 100%;
	}

	.column__subesctions-container {
		display: flex;
		flex-direction: column;
		gap: 16px;

		.column__subsection-title {
			font-size: 18px;
		}
	}
}
</style>
